
import axios from "axios";
import type { SearchResponse } from "@/types/product";
import type {
  FileUploadResponse,
  FileDetailsResponse,
  UploadedProduct,
} from "@/types/fileUpload";
import { store } from "@/store";
import { updateSubscriptionInfo } from "@/store/slices/authSlice";
import { setBlockedFeaturesMessage } from "@/store/slices/productSlice";

const API_BASE_URL = "https://api.thmn.ai/api";
const API_LOCAL_URL = "http://192.168.1.198:8000/api";

const baseApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

baseApi.interceptors.request.use((config) => {
  const state = store.getState();
  const token = state.auth.tokens.access;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

baseApi.interceptors.response.use(
  (response) => {
    if (response.data?.subscription || response.data?.subscription_usage) {
      store.dispatch(
        updateSubscriptionInfo({
          subscription: response.data.subscription || null,
          subscription_usage: response.data.subscription_usage || null,
        })
      );
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 403) {
      const blockedFeatures = error.response.data?.data?.blocked_features || 
      error.response.data?.data?.error?.detail?.blocked_features;

      if (blockedFeatures) {
        const message = Object.values(blockedFeatures)[0] as string;
        store.dispatch(setBlockedFeaturesMessage(message));
        window.location.href = "/upgrade";
      }
    }

    if (
      error.response?.data?.subscription ||
      error.response?.data?.subscription_usage
    ) {
      store.dispatch(
        updateSubscriptionInfo({
          subscription: error.response.data.subscription || null,
          subscription_usage: error.response.data.subscription_usage || null,
        })
      );
    }
    return Promise.reject(error);
  }
);

export const fetchSearchResults = async (
  query: string,
  page: number
): Promise<SearchResponse> => {
  // Mock implementation for now
  return {
    results: [],
    hasMore: false,
    currentPage: page,
    totalPages: 1,
  };
};

export const uploadProductsFile = async (
  products: UploadedProduct[]
): Promise<FileUploadResponse> => {
  const response = await baseApi.post(
    "/query-logs/upload-products-file/",
    products
  );
  return response?.data?.data;
};

export const getFileDetails = async (
  fileId: string
): Promise<FileDetailsResponse> => {
  const response = await baseApi.get(`/file-uploads/${fileId}/`);
  return response?.data?.data;
};

export const exportQueryLogs = async (params: {
  brand?: string;
  category?: string;
  search?: string;
}): Promise<Blob> => {
  const response = await baseApi.get("/query-logs/export/", {
    params,
    responseType: 'blob'
  });
  return response.data;
};

export const exportCompetitorsQueryLogs = async (params: {
  brand?: string;
  category?: string;
  search?: string;
  provider_ids?: string;
}): Promise<Blob> => {
  const response = await baseApi.get("/query-logs/competitors-export/", {
    params,
    responseType: 'blob'
  });
  return response.data;
};

export { baseApi };
